import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import ScrollView from '@/components/Common/ScrollView';
import Spinner from '@/components/Common/Spinner';
import ToggleRadio from '@/components/Common/ToggleRadio';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { modalStyle } from '@/styles/style';

import type { FrontInboxInfoT, FrontInfo } from '../types/types';
import FrontWorkspaceInboxItem from './FrontWorkspaceInboxItem';

type FrontInboxSettingModalProps = {
  workspace: FrontInfo;
  onUpdateInboxes: (params: {
    info: FrontInboxInfoT;
    action: 'push' | 'pull';
  }) => void;
  onUpdatePrivacy: (privacy: boolean) => void;
  onFetchInboxInfos: (
    options: unknown,
    callbacks: { onSuccess: (data: FrontInboxInfoT[]) => void },
  ) => void;
};

const FrontInboxSettingModal = (props: FrontInboxSettingModalProps) => {
  const { workspace, onUpdateInboxes, onUpdatePrivacy, onFetchInboxInfos } =
    props;
  const color = useColor();
  const { closeModal } = useModalContext();

  const [inboxInfos, setInboxInfos] = useState<FrontInboxInfoT[]>([]);
  const [localWorkspace, setLocalWorkspace] = useState<FrontInfo>(workspace);

  useEffect(() => {
    setLocalWorkspace(workspace);
  }, [workspace]);

  useEffect(() => {
    onFetchInboxInfos(undefined, {
      onSuccess: (data: FrontInboxInfoT[]) => {
        setInboxInfos(data);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentInfos = useMemo(() => {
    return localWorkspace.inbox_infos?.map((info) => info.id) || [];
  }, [localWorkspace]);

  const handleOnChangeActive = (info: FrontInboxInfoT) => {
    const newWorkspace = { ...localWorkspace };
    if (currentInfos.includes(info.id)) {
      onUpdateInboxes({ info, action: 'pull' });
      newWorkspace.inbox_infos = newWorkspace.inbox_infos?.filter(
        (inbox) => inbox.id !== info.id,
      );
    } else {
      newWorkspace.inbox_infos = newWorkspace.inbox_infos?.concat([info]);
      onUpdateInboxes({ info, action: 'push' });
    }
    setLocalWorkspace(newWorkspace);
  };

  const handleChangeCustomerImport = (privacy: boolean) => {
    onUpdatePrivacy(privacy);
    setLocalWorkspace({ ...localWorkspace, privacy_policy: privacy });
  };

  return (
    <ScrollView style={{ maxHeight: 'calc(100vh - 80px' }}>
      <Style d="column">
        <Heading type="h2" color={color.gray800}>
          Front Inboxes
        </Heading>
        <Space h="4px" />
        <Typo type="label2" color={color.gray700}>
          Select inboxes where your team would use Syncly
        </Typo>
        <Space h="24px" />
        <Flex className="channel-list-wrapper">
          {inboxInfos.length > 0 ? (
            <ScrollView style={{ width: '100%' }}>
              <Flex
                g="8px"
                j="space-between"
                style={{ flexWrap: 'wrap', width: '100%', padding: 24 }}
              >
                {inboxInfos.map((data) => (
                  <FrontWorkspaceInboxItem
                    info={data}
                    isActive={currentInfos.includes(data.id)}
                    onChangeActive={handleOnChangeActive}
                    key={data.name}
                  />
                ))}
              </Flex>
            </ScrollView>
          ) : (
            <Flex
              a="center"
              j="center"
              style={{ width: '100%', height: '100%' }}
              d="column"
              g="16px"
            >
              <Spinner />
              <Typo type="label2" color={color.gray500}>
                Loading Channel list...
              </Typo>
            </Flex>
          )}
        </Flex>
        <Space h="24px" />
        <Flex d="column" g="8px">
          <Heading type="h3">Customer data</Heading>
          <Typo type="body3" color={color.gray600}>
            It includes all the accounts and contacts in Front
          </Typo>
          <Flex g="12px" a="center">
            <ToggleRadio
              status={!localWorkspace.privacy_policy}
              onChange={(state) => handleChangeCustomerImport(!state)}
            />
            <MainText>Import customer data</MainText>
          </Flex>
        </Flex>
        <Flex j="flex-end">
          <Button onClick={closeModal}>Done</Button>
        </Flex>
      </Style>
    </ScrollView>
  );
};

export default FrontInboxSettingModal;

const Style = styled(Flex)`
  padding: 24px;
  width: 800px;
  ${modalStyle};
  .channel-list-wrapper {
    height: 400px;
    border: 1px solid ${(props) => props.theme.colors.gray300};
    border-radius: 8px;
  }
`;
